import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useServices } from "../app/services.ts";
import { LiveShare } from "../audio/liveShare.ts";
import { LiveTranscript, type LiveStatus, type Turn } from "../audio/liveTranscript.ts";
import { canRecord, MAX_DURATION_MS, Recorder } from "../audio/recorder.ts";
import { VideoTrack } from "../audio/videoTrack.ts";
import { duration } from "../data/display.ts";
import { kindOf, MAX_IMAGE_BYTES, MAX_MEDIA_BYTES } from "../data/upload.ts";
import { Icon } from "../ui/Icon.tsx";
import { LiveRecordings } from "./LiveRecordings.tsx";
import { GifPicker } from "../gif/GifPicker.tsx";
import { LocationPicker } from "../location/LocationPicker.tsx";
import { available as gifsAvailable, shared as gifShared } from "../gif/klipy.ts";
import { ImageEditor } from "../edit/ImageEditor.tsx";
import styles from "./Composer.module.css";

const LEVELS = 70;

/** Text, voice and files (docs/web-designs screens 2 and 3). [dropped] are files dragged onto the chat. */
export function Composer({ conversationId, dropped, onDroppedTaken }: { conversationId: string; dropped: File[]; onDroppedTaken: () => void }) {
  const services = useServices();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [recording, setRecording] = useState<{ ms: number; levels: number[]; paused: boolean; pushToTalk: boolean } | null>(null);
  const [voiceSupported, setVoiceSupported] = useState<boolean>();
  const [gifOpen, setGifOpen] = useState(false);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const recorder = useRef<Recorder | null>(null);
  const live = useRef<LiveTranscript | null>(null);
  const share = useRef<LiveShare | null>(null);
  const video = useRef<VideoTrack | null>(null);
  const preview = useRef<HTMLVideoElement>(null);
  const [camera, setCamera] = useState(false);
  const [transcript, setTranscript] = useState<{ status: LiveStatus; turns: Turn[] }>({ status: "idle", turns: [] });
  const lastActivity = useRef(0);
  const input = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  useEffect(() => void canRecord().then(setVoiceSupported), []);

  useEffect(() => {
    setText("");
    setFiles([]);
    setError(undefined);
    return () => {
      recorder.current?.cancel();
      live.current?.discard();
      share.current?.end("discarded");
      share.current = null;
      video.current?.cancel();
      video.current = null;
    };
  }, [conversationId]);

  async function toggleCamera() {
    const r = recorder.current;
    if (!r) return;
    const v = (video.current ??= new VideoTrack(() => r.durationMs));
    if (v.on) {
      v.stop();
      setCamera(false);
      return;
    }
    try {
      if (r.isPaused) togglePause();
      await v.start(preview.current);
      setCamera(true);
    } catch {
      setError("Die Kamera ließ sich nicht starten. Erlaube sie über das Symbol in der Adressleiste.");
    }
  }

  // Someone in the chat wants to read along: the live transcript starts (it runs on request only).
  useEffect(
    () =>
      services.realtime.subscribe((frame) => {
        if (frame.t !== "live" || frame.conversation !== conversationId || !recorder.current) return;
        if (frame.reading === true) startLive();
        // Someone listens in (or stopped): packets go out only while someone does.
        if (typeof frame.listening === "boolean" && typeof frame.user === "string") share.current?.onListener(frame.user, frame.listening);
      }),
    [conversationId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  function liveFrame(fields: Record<string, unknown>) {
    services.realtime.send({ t: "live", conversation: conversationId, ...fields });
  }

  function startLive() {
    const l = live.current;
    if (!l || l.status !== "idle") return;
    void l.start();
    share.current?.setText(true);
  }

  useEffect(() => {
    if (dropped.length === 0) return;
    addFiles(dropped);
    onDroppedTaken();
  }, [dropped]); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(list: File[]) {
    const ok = list.filter((f) => f.size <= (kindOf(f) === "image" ? MAX_IMAGE_BYTES : MAX_MEDIA_BYTES));
    if (ok.length < list.length) setError("Dateien über 100 MB (Bilder über 25 MB) gehen nicht.");
    setFiles((current) => [...current, ...ok].slice(0, 30));
  }

  function activity(what: "typing" | "recording" | "idle") {
    if (what !== "idle" && Date.now() - lastActivity.current < 4000) return;
    lastActivity.current = what === "idle" ? 0 : Date.now();
    services.realtime.send({ t: "activity", conversation: conversationId, what });
  }

  async function send() {
    if (busy || (!text.trim() && files.length === 0)) return;
    setBusy(true);
    setError(undefined);
    try {
      if (files.length > 0) {
        for (const [i, file] of files.entries()) await services.sendFile(conversationId, file, i === 0 ? text : undefined);
      } else {
        await services.sendText(conversationId, text);
      }
      setText("");
      setFiles([]);
      activity("idle");
    } catch {
      setError("Nicht gesendet. Bitte noch einmal versuchen.");
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  }

  async function startRecording(pushToTalk: boolean) {
    if (recorder.current || busy) return;
    setError(undefined);
    if (!voiceSupported) {
      setError("Dieser Browser kann keine Sprachnachrichten aufnehmen. Chrome, Edge und Firefox können es.");
      return;
    }
    const r = new Recorder();
    recorder.current = r;
    r.onLevel = (level, ms) => {
      setRecording((s) => s && { ...s, ms, levels: [...s.levels.slice(-(LEVELS - 1)), level] });
      activity("recording");
    };
    r.onLimit = () => void finishRecording(true);
    const l = new LiveTranscript(services.session);
    live.current = l;
    r.onPcm = (pcm) => l.feed(pcm);
    l.onChange = (turn) => {
      setTranscript({ status: l.status, turns: [...l.turns.values()].sort((a, b) => a.order - b.order) });
      if (turn) liveFrame({ turn: { o: turn.order, text: turn.text.slice(0, 4000), final: turn.final } });
    };
    setTranscript({ status: "idle", turns: [] });
    setRecording({ ms: 0, levels: [], paused: false, pushToTalk });
    const sh = new LiveShare(conversationId, (frame) => services.realtime.send(frame));
    share.current = sh;
    r.onPacket = (packet) => sh.packet(packet);
    try {
      await r.start();
    } catch (e) {
      recorder.current = null;
      live.current = null;
      share.current?.end("discarded");
      share.current = null;
      setRecording(null);
      setError((e as Error).name === "NotAllowedError" ? "Das Mikrofon ist nicht erlaubt. Erlaube es über das Symbol in der Adressleiste." : "Das Mikrofon ließ sich nicht starten.");
    }
  }

  async function finishRecording(send: boolean) {
    const r = recorder.current;
    if (!r) return;
    const l = live.current;
    const sh = share.current;
    const v = video.current;
    recorder.current = null;
    live.current = null;
    share.current = null;
    video.current = null;
    v?.stop();
    setCamera(false);
    setRecording(null);
    activity("idle");
    if (!send) {
      l?.discard();
      v?.cancel();
      sh?.end("discarded");
      setTranscript({ status: "idle", turns: [] });
      return r.cancel();
    }
    setBusy(true);
    try {
      l?.end();
      const result = await r.stop();
      sh?.end("ended");
      // A tap on the space bar is no message.
      if (result.durationMs < 700) {
        v?.cancel();
        return l?.discard();
      }
      const words = l && l.status !== "idle" ? await l.finish() : null;
      const track = v ? await v.finish(result.durationMs) : null;
      await services.sendVoice(conversationId, result, words, track);
      setTranscript({ status: "idle", turns: [] });
    } catch {
      setError("Die Sprachnachricht ging nicht raus. Bitte noch einmal aufnehmen.");
    } finally {
      setBusy(false);
    }
  }

  function togglePause() {
    const r = recorder.current;
    if (!r) return;
    if (r.isPaused) r.resume();
    else {
      r.pause();
      // A paused recording has no picture either.
      video.current?.stop();
      setCamera(false);
    }
    share.current?.setPaused(r.isPaused);
    setRecording((s) => s && { ...s, paused: r.isPaused });
  }

  // Push-to-talk: hold the space bar outside text fields (decision 17); P pauses, Esc discards.
  useEffect(() => {
    const typing = (target: EventTarget | null) => target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
    const down = (e: globalThis.KeyboardEvent) => {
      if (recorder.current) {
        if (e.key === "Escape") void finishRecording(false);
        else if (e.key.toLowerCase() === "p" && !typing(e.target)) togglePause();
        else if (e.key === "Enter") void finishRecording(true);
        return;
      }
      if (e.code === "Space" && !e.repeat && !typing(e.target) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        // A focused button (the chat just clicked in the list) would take the space as a click.
        if (e.target instanceof HTMLElement && e.target.tagName === "BUTTON") e.target.blur();
        void startRecording(true);
      } else if (e.key.length === 1 && e.key !== " " && !typing(e.target) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Typing anywhere in the chat writes into the message field (the space bar records).
        input.current?.focus();
      }
    };
    const up = (e: globalThis.KeyboardEvent) => {
      if (e.code === "Space" && recording?.pushToTalk) void finishRecording(true);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }); // re-bound every render: it reads the current state

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  }

  const panel = transcript.status !== "idle" && (
    <div className={styles.live}>
      <div className={styles.liveHead}>
        <Icon name="text" size={16} /> LIVE-TRANSKRIPT
        <span>{{ connecting: "verbindet …", live: "", finishing: "die letzten Worte …", done: "", failed: "unterbrochen – der Server transkribiert nach dem Senden", unavailable: "gerade nicht verfügbar", idle: "" }[transcript.status]}</span>
      </div>
      <p>
        {transcript.turns.map((t) => (
          <span key={t.order} className={t.final ? undefined : styles.partial}>
            {t.text}{" "}
          </span>
        ))}
        {transcript.status === "live" && <span className={styles.caret} />}
      </p>
    </div>
  );

  if (recording) {
    const left = MAX_DURATION_MS - recording.ms;
    return (
      <div className={styles.wrap}>
        {panel}
        <video ref={preview} className={camera ? styles.preview : styles.previewHidden} muted playsInline aria-label="Kamera" />
        <div className={styles.row}>
          <div className={styles.recording}>
            <span className={recording.paused ? styles.dotPaused : styles.dot} />
            <b className={styles.timer}>{duration(recording.ms)}</b>
            <div className={styles.levels} aria-hidden>
              {Array.from({ length: LEVELS }, (_, i) => {
                const level = recording.levels[i - (LEVELS - recording.levels.length)];
                return <span key={i} style={{ height: level === undefined ? 3 : Math.max(3, Math.min(1, level * 2.5) * 30) }} />;
              })}
            </div>
            {transcript.status === "idle" && (
              <button className={styles.read} onClick={startLive} title="Live mitlesen, was du sagst (Live-Transkript)">
                <Icon name="text" size={18} /> Mitlesen
              </button>
            )}
            <button className={camera ? `${styles.icon} ${styles.iconOn}` : styles.icon} onClick={() => void toggleCamera()} aria-label={camera ? "Kamera aus" : "Kamera an"} aria-pressed={camera}>
              <Icon name="video" />
            </button>
            <button className={styles.icon} onClick={togglePause} aria-label={recording.paused ? "Weiter aufnehmen" : "Pause"}>
              <Icon name={recording.paused ? "mic" : "pause"} />
            </button>
            <button className={styles.icon} onClick={() => void finishRecording(false)} aria-label="Verwerfen">
              <Icon name="close" />
            </button>
          </div>
          <button className={`${styles.round} ${styles.red}`} onClick={() => void finishRecording(true)} aria-label="Senden">
            <Icon name="send" size={24} />
          </button>
        </div>
        <p className={styles.hint}>
          {recording.pushToTalk ? (
            <>
              <kbd>Leertaste</kbd> loslassen: senden
            </>
          ) : (
            <>
              <kbd>Enter</kbd> senden
            </>
          )}{" "}
          · <kbd>Esc</kbd> verwerfen · <kbd>P</kbd> Pause{left < 60_000 ? ` · noch ${Math.ceil(left / 1000)} s` : ""}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <LiveRecordings conversationId={conversationId} />
      {gifOpen && (
        <GifPicker
          onClose={() => setGifOpen(false)}
          onPick={(gif, query) => {
            setGifOpen(false);
            setBusy(true);
            services
              .sendGif(conversationId, gif)
              .then(() => gifShared(gif, query), () => setError("Das GIF ging nicht raus."))
              .finally(() => setBusy(false));
          }}
        />
      )}
      {error && <p className={styles.error}>{error}</p>}
      {editing != null && files[editing] && (
        <ImageEditor
          source={files[editing]}
          onClose={() => setEditing(null)}
          onDone={({ file }) => {
            const name = files[editing]!.name.replace(/\.[^.]+$/, "") + ".jpg";
            setFiles(files.map((f, j) => (j === editing ? new File([file], name, { type: "image/jpeg" }) : f)));
            setEditing(null);
          }}
        />
      )}
      {files.length > 0 && (
        <div className={styles.files}>
          {files.map((f, i) => (
            <span key={i} className={styles.file}>
              <Icon name={kindOf(f) === "image" ? "image" : kindOf(f) === "video" ? "video" : "doc"} size={18} />
              <span>{f.name}</span>
              {kindOf(f) === "image" && f.type !== "image/gif" && (
                <button onClick={() => setEditing(i)} aria-label={`${f.name} bearbeiten`}>
                  <Icon name="edit" size={16} />
                </button>
              )}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`${f.name} entfernen`}>
                <Icon name="close" size={16} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className={styles.row}>
        <div className={styles.box}>
          <button className={styles.icon} onClick={() => picker.current?.click()} aria-label="Datei anhängen">
            <Icon name="attach" />
          </button>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              addFiles([...(e.target.files ?? [])]);
              e.target.value = "";
            }}
          />
          {"geolocation" in navigator && (
            <button className={styles.icon} onClick={() => setPlaceOpen(true)} aria-label="Standort">
              <Icon name="place" />
            </button>
          )}
          {placeOpen && <LocationPicker conversationId={conversationId} onClose={() => setPlaceOpen(false)} />}
          {gifsAvailable() && (
            <button className={styles.icon} onClick={() => setGifOpen(!gifOpen)} aria-label="GIF" aria-expanded={gifOpen}>
              <Icon name="gif" />
            </button>
          )}
          <textarea
            ref={input}
            rows={1}
            placeholder={files.length ? "Bildunterschrift (optional)" : "Nachricht schreiben"}
            value={text}
            maxLength={4000}
            onKeyDown={onKey}
            onPaste={(e) => {
              const pasted = [...e.clipboardData.files];
              if (pasted.length) {
                e.preventDefault();
                addFiles(pasted);
              }
            }}
            onChange={(e) => {
              setText(e.target.value);
              activity("typing");
            }}
          />
        </div>
        {text.trim() || files.length ? (
          <button className={styles.round} onClick={() => void send()} disabled={busy} aria-label="Senden">
            <Icon name="send" size={24} />
          </button>
        ) : (
          <button className={styles.round} onClick={() => void startRecording(false)} disabled={busy} aria-label="Sprachnachricht aufnehmen">
            <Icon name="mic" size={26} />
          </button>
        )}
      </div>
      <p className={styles.hint}>
        {busy ? (
          "Wird gesendet …"
        ) : (
          <>
            Zum Sprechen <kbd>Leertaste</kbd> halten oder auf das Mikrofon klicken · Dateien einfach hineinziehen
          </>
        )}
      </p>
    </div>
  );
}
